import React from "react"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { graphql, useStaticQuery } from "gatsby"
import { useTranslation } from "react-i18next"

// Single source for the agent page's own slug: the block both links to that page
// and has to recognise when it is being rendered ON it, and those two must never
// drift apart.
const AGENT_PAGE_SLUG = "ai-production-agent"

/**
 * "Powered by the AI Production Agent" section appended to every feature page.
 *
 * @param {object} props
 * @param {string} [props.slug] - slug of the page mounting the block. On the agent's
 *   own page the CTA is dropped and the heading swapped, so the section stops linking
 *   to the page the reader is already on and stops restating its H1.
 */
function AIProductionAgent({ slug }) {
  const { t } = useTranslation("home")
  const isAgentPage = slug === AGENT_PAGE_SLUG
  const capabilities = t("aiAgent.capabilities", { returnObjects: true })

  const data = useStaticQuery(graphql`
    query {
      file(
        sourceInstanceName: { eq: "images" }
        relativePath: { eq: "film/features/home/ai-dude-andrei.png" }
      ) {
        childImageSharp {
          gatsbyImageData(width: 500, quality: 90, layout: CONSTRAINED)
        }
      }
    }
  `)

  const image = data.file ? getImage(data.file.childImageSharp) : null

  return (
    <section>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="py-12 md:py-20 border-t border-gray-800">
          {/* Section header */}
          <div className="max-w-3xl mx-auto text-center pb-8 md:pb-10">
            <h2 className="h2 mb-4" data-aos="fade-up">
              {isAgentPage ? t("aiAgent.titleOnAgentPage") : t("aiAgent.title")}
            </h2>
          </div>

          {/* Content: text left, image right */}
          <div
            className="md:grid md:grid-cols-5 bg-gray-800 rounded shadow-2xl shadow-indigo-500/30 overflow-hidden"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            {/* Left: text */}
            <div className="p-6 md:py-6 md:pl-8 md:pr-4 col-span-3">
              <p className="text-xl text-gray-400 mb-4">
                {t("aiAgent.intro")}
              </p>

              <p className="text-gray-400 mb-4">
                {t("aiAgent.worksWith")}
              </p>

              <ul className="text-gray-400 space-y-2 mb-6">
                {capabilities.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <svg
                      className="w-4 h-4 fill-current text-purple-600 mr-3 mt-1.5 flex-shrink-0"
                      viewBox="0 0 16 16"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M13.707 2.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414L5 9.586l7.293-7.293a1 1 0 011.414 0z" />
                    </svg>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              {!isAgentPage && (
                <a
                  href={`/${AGENT_PAGE_SLUG}/`}
                  className="btn-sm capitalize bg-fPurple-800 hover:bg-fPurple-900 mt-6 inline-flex"
                >
                  <span className="text-sm">{t("aiAgent.meetButton")}</span>
                  <svg
                    className="w-3 h-3 fill-current text-purple-400 flex-shrink-0 ml-2"
                    viewBox="0 0 12 12"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M6 5H0v2h6v4l6-5-6-5z" />
                  </svg>
                </a>
              )}

              <div className="bg-gray-700/50 p-4 rounded mt-6">
                <p className="text-gray-300 italic">
                  {t("aiAgent.quote")}
                </p>
              </div>
            </div>

            {/* Right: image */}
            <div className="mt-6 md:mt-0 self-end col-span-2">
              {image && (
                <GatsbyImage
                  image={image}
                  alt="AI Dude - AI Production Agent"
                  className="!rounded-none"
                  imgStyle={{ objectFit: "contain", objectPosition: "bottom" }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default AIProductionAgent
